import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ArrowUp, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type LazyArtworkDTO } from "../../api";
import { ARTWORK_STATUS_VARIANT } from "../../components/badges";
import { EnumBadge, objectUrl, Thumbnail, TwoLine } from "../../components/cells";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * The artworks inside one Shopify collection, in merchandising order.
 *
 * Shopify keeps a manual sort per collection; this screen is where it is set.
 * "Move to top" exists because the everyday task is promoting a handful of
 * pieces to the front, which is tedious to do by dragging across a long list.
 */
export function ShopifyCollectionDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const { id } = useParams({ from: "/collection/shopify-collections/$id" });
  const mappingId = Number(id);
  const allowed = useAllowed(LogicView.COLLECTION_SHOPIFY);
  const title = t("navigation.item.shopify-collections", "Shopify");

  const [order, setOrder] = useState<LazyArtworkDTO[] | null>(null);
  const [selected, setSelected] = useState<LazyArtworkDTO[]>([]);
  const [saving, setSaving] = useState(false);

  const mapping = useQuery({
    queryKey: ["shopify-collection", mappingId],
    queryFn: () => api.shopifyCollections.findById(mappingId),
    enabled: allowed && Number.isFinite(mappingId),
  });

  const storeId = mapping.data?.store?.id ?? 0;

  const artworks = useQuery({
    queryKey: ["shopify-collection", mappingId, "artworks"],
    queryFn: () => api.shopifyCollections.getArtworks(storeId, mappingId),
    enabled: allowed && storeId > 0,
  });

  useEffect(() => {
    if (artworks.data) setOrder(artworks.data);
  }, [artworks.data]);

  const rows = order ?? [];

  const cols = useMemo(() => {
    const c = defineColumns<LazyArtworkDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => (
          <Thumbnail url={objectUrl(row.thumbnail?.image)} alt={row.metadata.code} fallback="na" />
        ),
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 56 },
      ),
      c.custom(
        "status",
        (row) => row.status,
        (row) => <EnumBadge value={row.status} variants={ARTWORK_STATUS_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.text("code", (row) => row.metadata.code, { headerKey: "column.code", priority: 1 }),
      c.custom(
        "artwork",
        (row) => `${row.metadata.title} ${row.metadata.artist ?? ""}`,
        (row) => <TwoLine primary={row.metadata.title} secondary={row.metadata.artist} />,
        { headerKey: "column.artwork", priority: 1 },
      ),
    ];
  }, []);

  const moveToTop = () => {
    if (selected.length === 0) return;
    const chosen = new Set(selected.map((row) => row.id));
    setOrder([...selected, ...rows.filter((row) => !chosen.has(row.id))]);
    setSelected([]);
  };

  const save = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.save.title", "Confirm save"),
      message: t("dialog.confirm.save.text", "Are you sure you want to save?"),
    });
    if (!ok) return;
    setSaving(true);
    try {
      const started = await api.shopifyCollections.reorder(
        storeId,
        mappingId,
        rows.map((row) => row.id),
      );
      if (started) {
        toast.success(
          t("notification.collection-reorder.success", "Collection reordered successfully"),
        );
      } else {
        toast.error(t("notification.collection-reorder.error", "Failed to reorder collection"));
      }
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.collection-reorder.error", "Failed to reorder collection"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (mapping.error) return <ErrorState title={title} error={mapping.error} />;

  return (
    <>
      <PageHeader
        parent={{ to: "/collection/shopify-collections", label: t("navigation.item.shopify-collections", "Shopify collections") }}
        title={`${mapping.data?.collection_id ?? `#${mappingId}`}`}
        badges={<Badge size="sm">{`${rows.length}`}</Badge>}
        actions={
          <div className="flex items-center gap-2">
<Button
              size="sm"
              variant="secondary"
              disabled={selected.length === 0}
              icon={<ArrowUp className="size-3.5" />}
              onClick={moveToTop}
            >
              {t("button.move-to-top", "Move to top")}
            </Button>
            <Button
              size="sm"
              variant="success"
              loading={saving}
              icon={<Save className="size-3.5" />}
              onClick={() => void save()}
            >
              {t("button.save", "Save")}
            </Button>
          </div>
        }
      />

      <DataTable<LazyArtworkDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={artworks.isLoading || mapping.isLoading}
        selectable
        onSelectionChange={setSelected}
        onReorder={setOrder}
      />
    </>
  );
}
